/**
 * SoftphoneDriver — the interface every browser-calling backend implements.
 *
 * The UI, the state machine and the fallback logic all talk to this and never to
 * a vendor SDK. That split is the reason the whole softphone could be built,
 * wired and tested before Exotel's WebSDK credentials existed: `StubDriver`
 * satisfies this contract exactly, and swapping in `ExotelDriver` changes one
 * factory line.
 *
 * ── Lifecycle ──
 *   register()   — connect and announce this agent's SIP identity. Until this
 *                  succeeds, calls placed in browser mode ring nowhere.
 *   unregister() — on logout, or when falling back to phone mode.
 *
 * ── Per-call ──
 *   answer() · hangup() · mute(on) · hold(on) · sendDtmf(digit)
 *
 * ── Events (emitted to the `onEvent` callback passed to register) ──
 *   { type: 'registering' }
 *   { type: 'registered' }
 *   { type: 'registration_failed', message }
 *   { type: 'unregistered' }
 *   { type: 'incoming',  call: { id, from, direction: 'inbound' } }
 *   { type: 'ringing',   call: { id, to,   direction: 'outbound' } }
 *   { type: 'answered',  call }
 *   { type: 'ended',     call, reason }
 *   { type: 'error',     message }
 *
 * Every method returns a promise and must never throw for an ordinary failure —
 * a softphone that throws mid-dial loses the call, and losing the call is the one
 * outcome browser mode is not allowed to produce. Report and let the caller fall
 * back to the counsellor's phone.
 */
export default class SoftphoneDriver {
  constructor() {
    this._onEvent = () => {};
  }

  /** Stable identifier, for logging and for telling drivers apart in the UI. */
  get name() { return "base"; }

  /**
   * Whether this driver can run here at all.
   *
   * Checked BEFORE any UI appears, so a missing SDK or an insecure origin
   * produces an explanation rather than a dead microphone button.
   *
   * @returns {Promise<{ ok: boolean, reason?: string }>}
   */
  async isAvailable() { return { ok: false, reason: "Not implemented." }; }

  /** @param {{ sipUri, sipIdentity, sipDomain, displayName, onEvent }} _ctx */
  async register(_ctx) { throw new Error(`${this.name}.register() not implemented`); }

  async unregister() {}

  async answer() {}

  async hangup() {}

  /** @param {boolean} _on */
  async mute(_on) {}

  /** @param {boolean} _on */
  async hold(_on) {}

  /** @param {string} _digit  0-9, * or # */
  async sendDtmf(_digit) {}

  /** Emit an event to whoever registered. Never lets a listener's error escape. */
  _emit(event) {
    try {
      this._onEvent?.(event);
    } catch (err) {
      console.error("[Softphone] listener threw:", err);
    }
  }
}

/**
 * Ask for the microphone, once, with the answer we actually need.
 *
 * Shared by every driver because the failure modes are identical and each one
 * needs its own sentence: "denied" is a browser setting the counsellor can fix,
 * "no device" is a headset that isn't plugged in, and an insecure origin is
 * neither — it is a deployment problem no amount of clicking will solve.
 *
 * @returns {Promise<{ ok: boolean, reason?: string, stream?: MediaStream }>}
 */
export async function requestMicrophone() {
  if (!window.isSecureContext) {
    return {
      ok: false,
      reason: "Browser calling needs a secure (https) connection. Open the CRM over https and try again.",
    };
  }
  if (!navigator.mediaDevices?.getUserMedia) {
    return { ok: false, reason: "This browser can't access a microphone. Use Chrome or Edge." };
  }

  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    return { ok: true, stream };
  } catch (err) {
    const name = err?.name || "";
    if (name === "NotAllowedError" || name === "SecurityError") {
      return {
        ok: false,
        reason: "Microphone access was blocked. Click the padlock in your address bar, "
          + "allow the microphone, then reload.",
      };
    }
    if (name === "NotFoundError" || name === "DevicesNotFoundError") {
      return { ok: false, reason: "No microphone found. Plug in a headset and try again." };
    }
    return { ok: false, reason: err?.message || "Could not open the microphone." };
  }
}
